import { CLEAR_AI_NOTES, SET_AI_NOTES_ERROR, SET_AI_NOTES_LOADING, SET_AI_NOTES_NEXT_SUMMARY_AT, SET_AI_NOTES_PANEL_VISIBLE, SET_AI_NOTES_SUMMARY, START_AI_NOTES_AUTO_SUMMARY, STOP_AI_NOTES_POLL } from './actionTypes';
import { AI_NOTES_HTTP_CONFLICT, AI_NOTES_SUMMARY_MODE_FINAL, AI_NOTES_SUMMARY_MODE_PARTIAL } from './constants';
import { fetchSummary, MeetingAiApiError, requestFinalizeSummary, requestPartialSummary } from './functions';
import logger from './logger';
/**
 * Store AI notes summary.
 *
 * @param {IAiNotesSummary | null} summary - Summary payload.
 * @returns {{ type: string, summary: IAiNotesSummary | null }}
 */
export function setAiNotesSummary(summary) {
    return {
        type: SET_AI_NOTES_SUMMARY,
        summary
    };
}
/**
 * Set loading flag.
 *
 * @param {boolean} loading - Loading.
 * @returns {{ type: string, loading: boolean }}
 */
export function setAiNotesLoading(loading) {
    return {
        type: SET_AI_NOTES_LOADING,
        loading
    };
}
/**
 * Set user-facing soft message（禁止裸 JSON）.
 *
 * @param {string | null} error - Soft message or null.
 * @returns {{ type: string, error: string | null }}
 */
export function setAiNotesError(error) {
    return {
        type: SET_AI_NOTES_ERROR,
        error
    };
}
/**
 * Clear AI notes state.
 *
 * @returns {{ type: string }}
 */
export function clearAiNotes() {
    return {
        type: CLEAR_AI_NOTES
    };
}
/**
 * 打开/关闭 AI 纪要侧栏。
 *
 * @param {boolean} visible - Visible.
 * @returns {{ type: string, visible: boolean }}
 */
export function setAiNotesPanelVisible(visible) {
    return {
        type: SET_AI_NOTES_PANEL_VISIBLE,
        visible
    };
}
/**
 * 打开 AI 纪要侧栏（由「更多」菜单触发）.
 *
 * @returns {{ type: string, visible: boolean }}
 */
export function openAiNotesPanel() {
    return setAiNotesPanelVisible(true);
}
/**
 * 关闭 AI 纪要侧栏.
 *
 * @returns {{ type: string, visible: boolean }}
 */
export function closeAiNotesPanel() {
    return setAiNotesPanelVisible(false);
}
/**
 * 设置下次自动总结时间.
 *
 * @param {number | null} nextSummaryAt - Epoch ms.
 * @returns {{ type: string, nextSummaryAt: number | null }}
 */
export function setAiNotesNextSummaryAt(nextSummaryAt) {
    return {
        type: SET_AI_NOTES_NEXT_SUMMARY_AT,
        nextSummaryAt
    };
}
/**
 * 请求停止自动总结定时器。
 *
 * @returns {{ type: string }}
 */
export function stopAiNotesPoll() {
    return {
        type: STOP_AI_NOTES_POLL
    };
}
/**
 * 启动/重置 2 分钟自动总结循环（middleware 处理）.
 *
 * @returns {{ type: string }}
 */
export function startAiNotesAutoSummary() {
    return {
        type: START_AI_NOTES_AUTO_SUMMARY
    };
}
/**
 * Refresh partial summary from meeting-ai-service.
 *
 * @param {{ force?: boolean }} [options] - force=true 绕过后端防抖（立即总结）.
 * @returns {Function}
 */
export function refreshPartialAiNotes(options) {
    const force = Boolean(options?.force);
    return async (dispatch, getState) => {
        const store = { dispatch, getState };
        dispatch(setAiNotesLoading(true));
        dispatch(setAiNotesError(null));
        try {
            const summary = await requestPartialSummary(store, { force });
            if (summary) {
                dispatch(setAiNotesSummary(summary));
            }
            // skipped / 无新发言：不回填旧稿，避免时间线误追加
        }
        catch (e) {
            if (e instanceof MeetingAiApiError && e.status === AI_NOTES_HTTP_CONFLICT) {
                logger.info('增量接口冲突，改拉已有纪要（不向用户展示裸错误）');
                try {
                    const finalSummary = await fetchSummary(store, AI_NOTES_SUMMARY_MODE_FINAL);
                    dispatch(setAiNotesSummary(finalSummary));
                }
                catch {
                    try {
                        const partial = await fetchSummary(store, AI_NOTES_SUMMARY_MODE_PARTIAL);
                        dispatch(setAiNotesSummary(partial));
                    }
                    catch {
                        // 静默
                    }
                }
                dispatch(setAiNotesError(null));
            }
            else {
                logger.warn('增量纪要刷新失败（静默）', e);
                // 会中不向用户抛技术错误 / 裸 JSON
                dispatch(setAiNotesError(null));
            }
        }
        finally {
            dispatch(setAiNotesLoading(false));
        }
    };
}
/**
 * Finalize meeting summary.
 *
 * @returns {Function}
 */
export function finalizeAiNotes() {
    return async (dispatch, getState) => {
        const store = { dispatch, getState };
        dispatch(setAiNotesLoading(true));
        dispatch(setAiNotesError(null));
        try {
            const summary = await requestFinalizeSummary(store);
            dispatch(stopAiNotesPoll());
            dispatch(setAiNotesSummary(summary));
        }
        catch (e) {
            logger.error('生成总纪要失败（尽量回填已有稿，不展示裸 JSON）', e);
            try {
                const existing = await fetchSummary(store, AI_NOTES_SUMMARY_MODE_FINAL);
                dispatch(setAiNotesSummary(existing));
                dispatch(setAiNotesError(null));
            }
            catch {
                dispatch(setAiNotesError(null));
            }
        }
        finally {
            dispatch(setAiNotesLoading(false));
        }
    };
}
